import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';

import { endpoints, type DesignArtifactKind } from '@/api/endpoints';
import { LoadingState, ErrorState } from '@/components/states';
import { formatCircuitLabel, formatLoadCategory, formatRcdGroupLabel } from '@/lib/electricalLabels';
import type {
  BoardDesignResult,
  Circuit,
  RCDGroup,
  ComplianceIssue,
  IssueSeverity,
  ProtectionDevice,
  BoardLayout,
  DxfArtifactRef,
  PreviewArtifactRef,
} from '@/types/api';

type Tab = 'single_line' | 'mounting' | 'json';

// ─── Issue severity badge ──────────────────────────────────────────────────

const SEVERITY_STYLES: Record<IssueSeverity, { cls: string; icon: string }> = {
  error:   { cls: 'border-danger/50 bg-danger/10 text-danger',       icon: '⨯' },
  warning: { cls: 'border-warning/50 bg-warning/10 text-warning', icon: '!' },
  info:    { cls: 'border-accent/50 bg-accent/10 text-accent',    icon: 'i' },
};

function IssueRow({ issue }: { issue: ComplianceIssue }) {
  const { cls, icon } = SEVERITY_STYLES[issue.severity];
  const relatedRefs = issue.related_refs ?? [];

  return (
    <div className={`flex gap-3 p-3 border-l-2 ${cls} bg-surface text-xs`}>
      <span className="font-mono font-bold flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-mono text-[11px] opacity-70">
            {issue.severity}
          </span>
          <code className="font-mono text-[11px] text-ink-3">{issue.code}</code>
        </div>
        <div className="text-ink">{issue.message}</div>
        {relatedRefs.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {relatedRefs.map(ref => (
              <code key={ref} className="font-mono text-[11px] text-ink-4">{ref}</code>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Layout summary tile ───────────────────────────────────────────────────

function StatTile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="border border-border rounded p-3 bg-surface-2">
      <div className="text-[11px] font-medium text-ink-4 mb-1">
        {label}
      </div>
      <div className="font-mono text-xl text-ink">{value}</div>
      {sub && <div className="font-mono text-[11px] text-ink-4 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Artifact downloads + metadata ───────────────────────────────────────────

type ArtifactRef = DxfArtifactRef | PreviewArtifactRef;

const ARTIFACT_LABELS: Record<DesignArtifactKind, string> = {
  single_line_preview: 'Single-line SVG',
  mounting_preview: 'Mounting SVG',
  single_line_dxf: 'Single-line DXF',
  mounting_dxf: 'Mounting DXF',
};

const DXF_DOWNLOADS: Array<{
  kind: Extract<DesignArtifactKind, 'single_line_dxf' | 'mounting_dxf'>;
  labelKey: string;
  filenameSuffix: string;
}> = [
  { kind: 'single_line_dxf', labelKey: 'design.download_single_line_dxf', filenameSuffix: 'single_line.dxf' },
  { kind: 'mounting_dxf', labelKey: 'design.download_mounting_dxf', filenameSuffix: 'mounting.dxf' },
];

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes)) return 'n/a';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

function shortHash(hash: string | null | undefined) {
  return hash ? hash.slice(0, 12) : 'n/a';
}

function downloadBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function ArtifactPanel({
  designId,
  artifacts,
}: {
  designId: string;
  artifacts?: Partial<Record<DesignArtifactKind, Partial<ArtifactRef> | null>> | null;
}) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState<DesignArtifactKind | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const artifactEntries = (Object.entries(artifacts ?? {}) as Array<[DesignArtifactKind, Partial<ArtifactRef> | null]>)
    .filter((entry): entry is [DesignArtifactKind, Partial<ArtifactRef>] => Boolean(entry[1]));

  const handleDownload = async (
    kind: Extract<DesignArtifactKind, 'single_line_dxf' | 'mounting_dxf'>,
    filenameSuffix: string,
  ) => {
    setDownloading(kind);
    setDownloadError(null);

    try {
      const blob = await endpoints.getArtifactBlob(
        endpoints.designArtifactPath(designId, kind),
        'application/dxf',
      );
      downloadBlob(blob, `${designId}_${filenameSuffix}`);
    } catch (err) {
      setDownloadError((err as Error).message || t('design.download_failed'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="border border-border rounded-lg bg-surface-2 overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-border bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-sans text-sm font-semibold text-ink">
            {t('design.artifacts')}
          </h3>
          <div className="text-xs text-ink-4">
            {t('design.artifacts_subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {DXF_DOWNLOADS.map(({ kind, labelKey, filenameSuffix }) => (
            <button
              key={kind}
              type="button"
              onClick={() => void handleDownload(kind, filenameSuffix)}
              disabled={downloading !== null}
              className="rounded-md border border-border bg-surface-2 px-3 py-2 text-xs font-medium text-ink
                         transition-colors hover:border-accent/50 hover:bg-surface-3
                         disabled:cursor-not-allowed disabled:opacity-60"
            >
              {downloading === kind ? t('design.downloading') : t(labelKey)}
            </button>
          ))}
        </div>
      </div>

      {downloadError && (
        <div className="border-b border-danger/30 bg-danger/10 px-4 py-2 text-xs text-danger">
          {t('design.download_failed')}: {downloadError}
        </div>
      )}

      {artifactEntries.length === 0 ? (
        <div className="px-4 py-3 text-xs text-ink-4">
          {t('design.no_artifact_metadata')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {artifactEntries.map(([kind, artifact]) => (
          <div key={kind} className="border-b border-border p-3 last:border-b-0 md:border-r md:last:border-r-0 xl:border-b-0">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink">{ARTIFACT_LABELS[kind]}</span>
              <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-[10px] text-ink-4">
                {artifact.kind ?? kind}
              </code>
            </div>
            <dl className="space-y-1 text-[11px]">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-4">{t('design.artifact_content_type')}</dt>
                <dd className="font-mono text-ink-3">{artifact.content_type ?? 'n/a'}</dd>
              </div>
              {'size_bytes' in artifact && typeof artifact.size_bytes === 'number' && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-4">{t('design.artifact_size')}</dt>
                  <dd className="font-mono text-ink-3">{formatBytes(artifact.size_bytes)}</dd>
                </div>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-ink-4">{t('design.artifact_sha')}</dt>
                <dd className="font-mono text-ink-3">{shortHash(artifact.sha256)}</dd>
              </div>
              {'source_dxf_sha256' in artifact && (
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-4">{t('design.artifact_source')}</dt>
                  <dd className="font-mono text-ink-3">{shortHash(artifact.source_dxf_sha256)}</dd>
                </div>
              )}
            </dl>
          </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Circuits table ────────────────────────────────────────────────────────

function CircuitsTable({ circuits, devices }: { circuits: Circuit[]; devices: ProtectionDevice[] }) {
  const { t } = useTranslation();
  const deviceMap = new Map(devices.map(d => [d.device_id, d]));

  if (circuits.length === 0) {
    return (
      <div className="border border-dashed border-border-2 rounded-lg p-6 text-xs text-ink-4">
        {t('design.no_circuits')}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-surface-2">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-xs">
        <thead>
          <tr className="border-b border-border bg-surface">
            {[
              t('design.table_id'),
              t('design.table_label'),
              t('design.table_category'),
              t('design.table_loads'),
              t('design.table_mcb'),
              t('design.table_flags'),
            ].map(h => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-medium text-ink-4">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {circuits.map(c => {
            const mcb = deviceMap.get(c.protection_device_id);
            return (
              <tr key={c.circuit_id} className="border-b border-border last:border-0
                                                  hover:bg-surface-3/50 transition-colors">
                <td className="px-3 py-2 font-mono text-ink-3">{c.circuit_id.replace('circuit_rebt_', '')}</td>
                <td className="px-3 py-2 text-ink">{formatCircuitLabel(t, c)}</td>
                <td className="px-3 py-2">
                  <span className="text-[11px] text-ink-3">{formatLoadCategory(t, c.category)}</span>
                </td>
                <td className="px-3 py-2 font-mono text-ink-4">{c.load_ids.length}</td>
                <td className="px-3 py-2 font-mono text-ink-3">
                  {mcb ? `${mcb.rating_a}A · ${mcb.modules_width}M` : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {c.is_dedicated && <Flag color="amber">{t('design.flag_dedicated')}</Flag>}
                    {c.is_wet_zone && <Flag color="cyan">{t('design.flag_wet')}</Flag>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function Flag({ color, children }: { color: 'amber' | 'cyan'; children: React.ReactNode }) {
  const cls = color === 'amber'
    ? 'bg-warning/15 text-warning border-warning/30'
    : 'bg-accent/15 text-accent border-accent/30';
  return (
    <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded border ${cls}`}>
      {children}
    </span>
  );
}

// ─── Groups table ──────────────────────────────────────────────────────────

function GroupsTable({ groups, devices }: { groups: RCDGroup[]; devices: ProtectionDevice[] }) {
  const { t } = useTranslation();
  const deviceMap = new Map(devices.map(d => [d.device_id, d]));

  if (groups.length === 0) {
    return (
      <div className="border border-dashed border-border-2 rounded-lg p-6 text-xs text-ink-4">
        {t('design.no_groups')}
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-surface-2">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] text-xs">
        <thead>
          <tr className="border-b border-border bg-surface">
            {[
              t('design.table_group'),
              t('design.table_label'),
              t('design.table_protection'),
              t('design.table_circuits'),
            ].map(h => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-medium text-ink-4">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g, groupIndex) => {
            const dev = deviceMap.get(g.protection_device_id);
            return (
              <tr key={g.group_id} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-mono text-ink-3">{g.group_id}</td>
                <td className="px-3 py-2 text-ink truncate max-w-[200px]">{formatRcdGroupLabel(t, groupIndex)}</td>
                <td className="px-3 py-2 font-mono text-ink-3">
                  {dev ? (
                    <>
                      {dev.device_type.toUpperCase()} {dev.rating_a}A
                      {dev.rcd_sensitivity_ma && ` · ${dev.rcd_sensitivity_ma}mA`}
                    </>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 font-mono text-ink-4">
                  {t('electrical.circuit_count', { count: g.circuit_ids.length })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ─── SVG tabs ──────────────────────────────────────────────────────────────

function SvgTabs({ designId, layout }: { designId: string; layout: BoardLayout }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('single_line');

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'single_line', label: t('design.tab_single_line') },
    { id: 'mounting',    label: t('design.tab_mounting') },
    { id: 'json',        label: t('design.tab_json') },
  ];

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-surface-2">
      {/* Tab bar */}
      <div className="flex border-b border-border bg-surface">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex-1 text-xs font-medium py-3
                        transition-colors border-b-2
                        ${tab === id
                          ? 'border-accent text-accent bg-accent/5'
                          : 'border-transparent text-ink-3 hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4">
        {tab === 'single_line' && (
          <SvgViewer
            src={endpoints.designArtifactPath(designId, 'single_line_preview')}
            alt="Single-line schematic"
          />
        )}
        {tab === 'mounting' && (
          <SvgViewer
            src={endpoints.designArtifactPath(designId, 'mounting_preview')}
            alt="Mounting layout"
          />
        )}
        {tab === 'json' && (
          <JsonInspector designId={designId} layout={layout} />
        )}
      </div>
    </div>
  );
}

function SvgViewer({ src, alt }: { src: string; alt: string }) {
  const [enlarged, setEnlarged] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let nextObjectUrl: string | null = null;

    setEnlarged(false);
    setObjectUrl(null);
    setError(null);

    endpoints.getSvgBlob(src)
      .then(blob => {
        if (cancelled) return;
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
      })
      .catch(err => {
        if (cancelled) return;
        setError((err as Error).message || 'SVG render failed');
      });

    return () => {
      cancelled = true;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [src]);

  if (error) {
    return (
      <div className="bg-surface border border-border rounded p-4 text-sm text-danger">
        SVG could not be loaded.
      </div>
    );
  }

  if (!objectUrl) {
    return (
      <div className="bg-white rounded p-8 text-center text-xs text-ink-4">
        Loading SVG...
      </div>
    );
  }

  return (
    <>
      <div className="bg-canvas border border-border rounded-lg p-4 overflow-auto">
        <img
          src={objectUrl}
          alt={alt}
          onClick={() => setEnlarged(true)}
          className="w-full h-auto cursor-zoom-in max-h-[600px] object-contain"
        />
      </div>

      {enlarged && (
        <div
          onClick={() => setEnlarged(false)}
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-8 cursor-zoom-out"
        >
          <img src={objectUrl} alt={alt} className="max-w-full max-h-full object-contain bg-white p-2 rounded" />
        </div>
      )}
    </>
  );
}

function JsonInspector({ designId, layout }: { designId: string; layout: BoardLayout }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-ink-3">
        Full design JSON id: <code className="font-mono text-ink">{designId}</code>
      </div>
      <pre className="bg-surface border border-border rounded p-4 text-[11px]
                       font-mono text-ink-3 overflow-auto max-h-96">
{JSON.stringify(layout, null, 2)}
      </pre>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export function DesignPage() {
  const { id } = useParams();
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: ['design', id],
    queryFn: () => endpoints.getDesign(id!),
    enabled: !!id,
  });

  if (!id) return <div className="text-danger">Invalid design id</div>;

  return (
    <div className="animate-fade-up">
      <Link to="/projects" className="text-xs font-medium text-ink-3 hover:text-accent
                              transition-colors mb-4 inline-block">
        ← {t('nav.dashboard')}
      </Link>

      {query.isLoading && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {query.isSuccess && <DesignContent design={query.data} />}
    </div>
  );
}

function DesignContent({ design }: { design: BoardDesignResult }) {
  const { t } = useTranslation();
  const { layout } = design;
  const circuits = design.circuits ?? [];
  const groups = design.groups ?? [];
  const issues = design.issues ?? [];
  const warnings = design.warnings ?? [];
  const degradedReason = design.degraded_reason;
  const devices = layout.devices ?? [];
  const modules = layout.modules ?? [];

  const reserveCount = modules.filter(m => m.is_reserve).length;
  const issuesByLevel = {
    error:   issues.filter(i => i.severity === 'error').length,
    warning: issues.filter(i => i.severity === 'warning').length,
    info:    issues.filter(i => i.severity === 'info').length,
  };

  return (
    <>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-sans text-xl font-semibold tracking-[-0.015em] mb-1">
          {design.project_label}
        </h1>
        <div className="flex flex-wrap gap-3 text-xs text-ink-3">
          <span>design: <code className="font-mono text-ink">{design.design_id.slice(0, 8)}</code></span>
          <span>created: <span className="font-mono text-ink">{new Date(design.created_at).toLocaleString()}</span></span>
        </div>
      </div>

      {(degradedReason || warnings.length > 0) && (
        <div className="mb-6 border border-warning/40 bg-warning/10 rounded-lg p-4">
          <h3 className="font-sans text-sm font-semibold text-warning mb-2">
            Warnings
          </h3>
          {degradedReason && (
            <div className="text-sm text-ink mb-2">{degradedReason}</div>
          )}
          {warnings.length > 0 && (
            <div className="space-y-1">
              {warnings.map(warning => (
                <div key={warning} className="text-xs text-ink-3">{warning}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <StatTile
          label={t('design.circuits')}
          value={circuits.length}
        />
        <StatTile
          label={t('design.groups')}
          value={groups.length}
        />
        <StatTile
          label={t('design.modules')}
          value={`${layout.total_modules_used}/${layout.rows * layout.modules_per_row}`}
          sub={`${layout.rows} row${layout.rows > 1 ? 's' : ''} × ${layout.modules_per_row}`}
        />
        <StatTile
          label={t('design.reserve')}
          value={reserveCount}
          sub={`${Math.round(100 * reserveCount / Math.max(1, layout.total_modules_used))}%`}
        />
        <StatTile
          label={t('design.fits')}
          value={layout.fits_capacity ? '✓ yes' : '✗ no'}
        />
        <StatTile
          label={t('design.issues')}
          value={issues.length}
          sub={
            issues.length === 0
              ? 'clean'
              : `${issuesByLevel.error}E · ${issuesByLevel.warning}W · ${issuesByLevel.info}I`
          }
        />
      </div>

      {/* Issues panel */}
      {issues.length > 0 && (
        <div className="mb-6">
          <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
            {t('design.issues')} ({issues.length})
          </h3>
          <div className="space-y-2">
            {[...issues]
              .sort((a, b) => {
                const order = { error: 0, warning: 1, info: 2 };
                return order[a.severity] - order[b.severity];
              })
              .map(i => <IssueRow key={i.issue_id} issue={i} />)}
          </div>
        </div>
      )}

      {/* SVG tabs */}
      <div className="mb-6">
        <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
          {t('design.schematics')}
        </h3>
        <SvgTabs designId={design.design_id} layout={layout} />
      </div>

      {/* Artifact downloads + metadata */}
      <div className="mb-6">
        <ArtifactPanel designId={design.design_id} artifacts={design.artifacts} />
      </div>

      {/* Tables: circuits + groups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
            {t('design.circuits')} ({circuits.length})
          </h3>
          <CircuitsTable circuits={circuits} devices={devices} />
        </div>

        <div>
          <h3 className="font-sans text-sm font-semibold text-ink-3 mb-3">
            {t('design.groups')} ({groups.length})
          </h3>
          <GroupsTable groups={groups} devices={devices} />
        </div>
      </div>
    </>
  );
}
